// Kurze Rückmeldungen unten links („Gespeichert“, Fehler mit nächstem Schritt).
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { fehlerInfo } from "./Fehler";
import { Icon } from "./Icon";

type Art = "ok" | "fehler" | "info";

interface Aktion {
  label: string;
  onClick?: () => void;
  to?: string;
}

interface Meldung {
  id: number;
  art: Art;
  text: string;
  aktion?: Aktion;
}

interface MeldungWert {
  zeige: (art: Art, text: string, aktion?: Aktion, dauerMs?: number) => void;
  ok: (text: string, aktion?: Aktion) => void;
  fehler: (e: unknown, vorab?: string) => void;
}

const Kontext = createContext<MeldungWert | null>(null);

export function MeldungenProvider({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<Meldung[]>([]);
  const naechste = useRef(1);
  const navigate = useNavigate();

  const weg = useCallback((id: number) => setListe((l) => l.filter((m) => m.id !== id)), []);

  const zeige = useCallback(
    (art: Art, text: string, aktion?: Aktion, dauerMs?: number) => {
      const id = naechste.current++;
      setListe((l) => [...l.filter((m) => m.text !== text).slice(-3), { id, art, text, aktion }]);
      const dauer = dauerMs ?? (art === "fehler" ? 9000 : aktion ? 7000 : 3500);
      window.setTimeout(() => weg(id), dauer);
    },
    [weg],
  );

  const wert = useMemo<MeldungWert>(
    () => ({
      zeige,
      ok: (text, aktion) => zeige("ok", text, aktion),
      fehler: (e, vorab) => {
        const info = fehlerInfo(e);
        zeige("fehler", vorab ? `${vorab}: ${info.text}` : info.text, info.link ? { label: info.link.label, to: info.link.to } : undefined);
      },
    }),
    [zeige],
  );

  return (
    <Kontext.Provider value={wert}>
      {children}
      <div className="meldungen" aria-live="polite">
        {liste.map((m) => (
          <div key={m.id} className={`meldung meldung--${m.art}`} role={m.art === "fehler" ? "alert" : "status"}>
            <Icon name={m.art === "fehler" ? "warnung" : m.art === "ok" ? "haken" : "info"} groesse={16} />
            <span className="meldung__text">{m.text}</span>
            {m.aktion ? (
              <button
                type="button"
                className="meldung__aktion"
                onClick={() => {
                  weg(m.id);
                  if (m.aktion?.to) navigate(m.aktion.to);
                  m.aktion?.onClick?.();
                }}
              >
                {m.aktion.label}
              </button>
            ) : null}
            <button type="button" className="meldung__zu" aria-label="Schließen" onClick={() => weg(m.id)}>
              <Icon name="schliessen" groesse={14} />
            </button>
          </div>
        ))}
      </div>
    </Kontext.Provider>
  );
}

export function useMeldung(): MeldungWert {
  const wert = useContext(Kontext);
  if (!wert) throw new Error("useMeldung außerhalb von MeldungenProvider");
  return wert;
}
